import React from 'react'
import { DIALOG_MENU_PROPS } from '../ravenV5Theme'
import { Button } from '@atd/rui-base.mui.button'
import { FormControl } from '@atd/rui-base.mui.form-control'
import { FormControlLabel } from '@atd/rui-base.mui.form-control-label'
import { FormHelperText } from '@atd/rui-base.mui.form-helper-text'
import { IconButton } from '@atd/rui-base.mui.icon-button'
import { InputLabel } from '@atd/rui-base.mui.input-label'
import { MenuItem } from '@atd/rui-base.mui.menu-item'
import { Radio } from '@atd/rui-base.mui.radio'
import { RadioGroup } from '@atd/rui-base.mui.radio-group'
import { Select } from '@atd/rui-base.mui.select'
import ValidatedTextField from '../commonComponents/validatedTextField'
import { connect, Provider, ReactReduxContext } from 'react-redux'
import { bindActionCreators } from 'redux'

import ResponsiveDialog from '../commonComponents/responsiveDialog'
import { DialogActions } from '@atd/rui-base.mui.dialog-actions'
import { DialogContent } from '@atd/rui-base.mui.dialog-content'
import DialogHeader from '../commonComponents/dialogHeader'

import AddIcon from '@mui/icons-material/Add'

import IngredientEdit from './ingredientCreation'
import MixIngredient from './mixCreationIngredient'

import * as ingredientActions from './ingredientActions'
import * as mixActions from './mixActions'

import { mixIngredientPrototype, ingredientPrototype } from './productPrototypes'
import { ddiFromDdiAndEntryMode, mixEntryModeFromDdi } from '../lib/ddiConversions'
import {
    checkProductLimits,
    ddiFromUnit,
    ddiFromMixState,
    getUnitEquivalentForDDI,
    convertToBase,
} from '../lib/ddiUnits'
import { getMenuItemsForProductDDI, getMenuItemsForProductEntryMode } from '../commonComponents/unitMenuItems'
import { emptyForNull } from '../lib/formData'

import './productConfigStyles.css'
import {selectIngredients} from './productReducer'

function mapStateToProps(state, ownProps) {
    return {
        ingredients: selectIngredients(state),
        carriers: state.product.carriers,
    }
}

function mapDispatchToProps(dispatch) {
    return {
        ...bindActionCreators(ingredientActions, dispatch),
        ...bindActionCreators(mixActions, dispatch),
    }
}

class MixEdit extends React.Component {
    static defaultProps = {
        data: {
            id: -1,
            name: '',
            carrierId: null,
            carrierQuantity: 0,
            carrierDisplayQuantity: 0,
            userDisplayUnit: 'gal/ac',
            mixIngredients: [new mixIngredientPrototype('')],
            ddiId: 1,
        },
        disabled: false,
    }

    state = {
        open: true,
        ...emptyForNull(this.props.data),
        entryMode: mixEntryModeFromDdi(this.props.data.ddiId),
        errorMessage: '',
        ingredientDialogOpen: false,
        mixIngredientId: null,
        showCarrier: (this.props.data.guid && this.props.data.carrierId),
        tempIngredient: {},
    }

    componentDidMount() {
        if (!this.state.mixIngredients) { return }

        // add contextually unique identifiers to pre-existing mix ingredients
        const mixIngredients = [...this.state.mixIngredients]
        let i = 0
        for (const mixIng of mixIngredients) {
            if (!mixIng.id) {
                mixIng.id = `${mixIng.ingredientId}-${i}`
                ++i
            }
        }
        this.setState({ mixIngredients })
    }

    renderIngredient = (data, i) => {
        return (
            <MixIngredient
                key={data.id + ' ' + data.ingredientId + data.userDisplayUnit}
                data={{...data}}
                mode={this.state.entryMode}
                selectIngredient={this.setIngredient}
                setIngredientProperty={this.setIngredientProperty}
                delete={this.handleDelete}
                addIngredient={this.handleNewIngredient}
                disabled={this.props.disabled}
            />
        )
    }

    handleNewIngredient = id => {
        this.setState({
            tempIngredient: new ingredientPrototype(''),
            ingredientDialogOpen: true,
            mixIngredientId: id,
        })
    }
    handleNewIngredientSave = newData => {
        this.props.addIngredient(newData).then(savedIngredient => {
            if (!savedIngredient || !savedIngredient.id) { return }
            const i = this.state.mixIngredients.findIndex(ing => { return ing.id === this.state.mixIngredientId })
            const tempIngredients = [...this.state.mixIngredients]
            if (i !== -1) {
                tempIngredients[i].ingredientId = savedIngredient.id
                tempIngredients[i].name = newData.name
                tempIngredients[i].ddiId = ddiFromDdiAndEntryMode(newData.ddiId, this.state.entryMode)
                tempIngredients[i].userDisplayUnit = getUnitEquivalentForDDI(newData.defaultDisplayUnit, tempIngredients[i].ddiId)
            }
            this.setState({
                ingredientDialogOpen: false,
                mixIngredientId: null,
                tempIngredient: {},
                mixIngredients: tempIngredients,
            })
        })
    }
    handleEditCancel = () => {
        this.setState({
            ingredientDialogOpen: false,
        })
    }
    handleTextChange = (event) => {
        const formData = {...this.state}
        formData[event.target.name] = event.target.value
        this.setState(formData)
    }
    handleDelete = id => {
        const tempIngredients = this.state.mixIngredients.filter(ingredient => ingredient.id !== id)
        this.setState({ mixIngredients: tempIngredients })
    }
    requestClose = (onComplete) => {
        this.exitCallback = onComplete
        this.setState({open: false})
    }
    handleSubmit = () => {
        const ingSet = new Set([])
        this.state.mixIngredients.forEach(mi => ingSet.add(mi.ingredientId))
        if (this.state.mixIngredients.length != ingSet.size) {
            this.setState({errorMessage: 'Duplicate Ingredients'})

            return
        }

        let invalidIngredients = false
        let ingredientTotalInBaseUnits = 0
        const carrierQtyInBaseUnits = convertToBase(this.state.carrierDisplayQuantity, this.state.userDisplayUnit)
        for (const ing of this.state.mixIngredients) {
            if (ing.id <= 0 || !ing.userDisplayUnit ||
                checkProductLimits(ing.displayQuantity, ing.userDisplayUnit)
            ) {
                invalidIngredients = true
                break
            }
            ingredientTotalInBaseUnits += convertToBase(ing.displayQuantity, ing.userDisplayUnit)
        }

        if (invalidIngredients ||
            !this.state.name || this.state.name.length > 32 ||
            !this.state.userDisplayUnit ||
            (this.state.showCarrier &&
                (!this.state.carrierId ||
                checkProductLimits(this.state.carrierDisplayQuantity, this.state.userDisplayUnit)))
        ) {
            this.setState({errorMessage: 'Invalid Data'})
        }
        else if (this.state.showCarrier && (carrierQtyInBaseUnits < ingredientTotalInBaseUnits)) {
            this.setState({errorMessage: 'Ingredient Rates cannot exceed Mix Rate'})
        }
        else {
            this.requestClose(() => this.props.onSave({ ...this.state }))
        }
    }

    handleCancel = () => {
        this.setState({
            id: -1,
            name: '',
            mixIngredients: [new mixIngredientPrototype()],
            entryMode: 'rateMode',
        })
        this.requestClose(this.props.onCancel)
    }
    addIngredient = () => {
        this.setState({ mixIngredients: [...this.state.mixIngredients, new mixIngredientPrototype()] })
    }
    setIngredient = (id, ingId) => {
        const tempIngredients = [...this.state.mixIngredients]
        const i = tempIngredients.findIndex(element => {
            return element.id === id
        })
        const selectedIngredient = this.props.ingredients.find(ing => { return ing.id === ingId })
        if (selectedIngredient === undefined) {
            return
        }
        tempIngredients[i].ingredientId = ingId
        tempIngredients[i].name = selectedIngredient.name
        tempIngredients[i].ddiId = ddiFromDdiAndEntryMode(selectedIngredient.ddiId, this.state.entryMode)
        tempIngredients[i].userDisplayUnit = getUnitEquivalentForDDI(selectedIngredient.defaultDisplayUnit, tempIngredients[i].ddiId)

        this.setState({ mixIngredients: tempIngredients })

        const ingSet = new Set([])
        tempIngredients.forEach(mi => ingSet.add(mi.ingredientId))
        if (tempIngredients.length != ingSet.size) {
            this.setState({errorMessage: 'Duplicate Ingredients'})
        }
    }

    setIngredientProperty = (id, property, value) => {
        const tempIngredients = [...this.state.mixIngredients]
        const i = tempIngredients.findIndex(element => {
            return element.id === id
        })
        if (i === -1) { return }
        tempIngredients[i][property] = value
        this.setState({ mixIngredients: tempIngredients })
    }

    handleModeChange = (event, entryMode) => {
        const tempIngredients = [...this.state.mixIngredients]
        for (const selectedIngredient of tempIngredients) {
            selectedIngredient.ddiId = ddiFromDdiAndEntryMode(selectedIngredient.ddiId, entryMode)
            selectedIngredient.userDisplayUnit = getUnitEquivalentForDDI(selectedIngredient.userDisplayUnit, selectedIngredient.ddiId)
        }
        const ddi = ddiFromDdiAndEntryMode(this.state.ddiId, entryMode)
        const displayUnit = getUnitEquivalentForDDI(this.state.userDisplayUnit, ddi)

        this.setState({ entryMode: entryMode, ddiId: ddi, userDisplayUnit: displayUnit, mixIngredients: tempIngredients })
    }

    handleCarrierId = (event, index, value) => {
        this.setState({ carrierId: value })
    }
    handleUserDisplayUnit = (event, index, value) => {
        const ddi = ddiFromUnit(value)

        this.setState({ userDisplayUnit: value, ddiId: ddi })
    }

    getDefaultCarrierId = () => {
        if (!this.props.carriers.length) { return null }
        const carrier = this.props.carriers.find(carrier => {
            return carrier.name == 'Water'
        })
        if (carrier) { return carrier.id }

        return this.props.carriers[0].id
    }
    toggleCarrier = () => {
        const showCarrier = !this.state.showCarrier
        const ddi = ddiFromMixState(this.state.entryMode, this.state.userDisplayUnit, showCarrier)
        const carrierId = showCarrier ? this.getDefaultCarrierId() : null
        const displayUnit = getUnitEquivalentForDDI(this.state.userDisplayUnit, ddi)
        this.setState({
            ddiId: ddi,
            showCarrier: showCarrier,
            carrierId: carrierId,
            carrierDisplayQuantity: 0.0,
            userDisplayUnit: displayUnit,
        })
    }

    ingredientDialog = () => {
        if (this.state.ingredientDialogOpen) {
            return (
                <IngredientEdit
                    data={this.state.tempIngredient}
                    open={this.state.ingredientDialogOpen}
                    onCancel={this.handleEditCancel}
                    onSave={this.handleNewIngredientSave}
                />
            )
        }
    }

    getCarrierSection = () => {
        if (this.state.showCarrier) {
            return this.renderCarrierLine()
        }

        return this.renderNoCarrierInfo()
    }

    renderNoCarrierInfo = () => {
        const units = getMenuItemsForProductEntryMode(this.state.entryMode)
        let addButton = null
        if (!this.props.disabled) {
            addButton = (
                <Button variant="contained"
                    startIcon={<AddIcon />}
                    onClick={this.toggleCarrier}
                    style={{marginTop: '12px'}}
                    className="addCarrierButton"
                    name="addCarrierButton">{'Add Carrier'}</Button>
            )
        }

        return (
            <div name="noCarrierSection">
                <div className="mixFlex">
                    <div style={{paddingBottom: '4px'}}>{`Mix Units: `}</div>
                    <FormControl variant="standard" className="unitSelectionInline" disabled={this.props.disabled} error={!this.state.userDisplayUnit}>
                        <Select
                            MenuProps={DIALOG_MENU_PROPS}
                            value={this.state.userDisplayUnit}
                            onChange={(event) => this.handleUserDisplayUnit(event, null, event.target.value)}
                            name="mixUnitSelect"
                        >
                            {units}
                        </Select>
                        {!this.state.userDisplayUnit && <FormHelperText>Required Field</FormHelperText>}
                    </FormControl>
                </div>
                {addButton}
            </div>
        )
    }
    renderCarrierLine = () => {
        const carrierList = this.createCarrierDropDownList()
        const units = getMenuItemsForProductDDI(this.state.ddiId)
        const errorText = checkProductLimits(this.state.carrierDisplayQuantity, this.state.userDisplayUnit)

        let deleteButton = null
        if (!this.props.disabled) {
            deleteButton = (
                <IconButton
                    className="ingredientDelete"
                    name="removeCarrier"
                    onClick={this.toggleCarrier}>
                    <img className="standardIcon" src={`${process.env.PUBLIC_URL}/images/jobIcons/Job_DeleteBlack.svg`}/>
                </IconButton>
            )
        }

        return (
            <div className="mixFlex" name="carrierSection">
                <FormControl variant="standard" className="selectFieldInline" sx={{top: '0px'}} disabled={this.props.disabled} error={this.state.carrierId <= 0}>
                    <InputLabel>Carrier</InputLabel>
                    <Select
                        MenuProps={DIALOG_MENU_PROPS}
                        value={this.state.carrierId > 0 ? this.state.carrierId : ''}
                        onChange={(event) => this.handleCarrierId(event, null, event.target.value)}
                        label="Carrier"
                        name="carrierSelect"
                    >
                        {carrierList}
                    </Select>
                    {this.state.carrierId <= 0 && <FormHelperText>Required Field</FormHelperText>}
                </FormControl>
                <ValidatedTextField
                    label={this.state.entryMode === 'rateMode' ? 'Mix Rate' : 'Volume'}
                    onChange={this.handleTextChange}
                    value={this.state.carrierDisplayQuantity || ''}
                    name="carrierDisplayQuantity"
                    className="flexSmall"
                    error={!!errorText}
                    helperText={errorText || ''}
                    disabled={this.props.disabled}
                />
                <FormControl variant="standard" className="unitSelectionInline" disabled={this.props.disabled} error={!this.state.userDisplayUnit}>
                    <Select
                        MenuProps={DIALOG_MENU_PROPS}
                        value={this.state.userDisplayUnit}
                        onChange={(event) => this.handleUserDisplayUnit(event, null, event.target.value)}
                        name="carrierUnits"
                    >
                        {units}
                    </Select>
                    {!this.state.userDisplayUnit && <FormHelperText>Required Field</FormHelperText>}
                </FormControl>
                {deleteButton}
            </div>
        )
    }
    createCarrierDropDownList = () => {
        return [
            ...this.props.carriers.map((data, i) => {
                return <MenuItem key={data.id} value={data.id} name={data.id}>{data.name}</MenuItem>
            }),
        ]
    }

    renderDialogActions = () => {
        if (this.props.disabled) {
            return [
                <Button key="close" variant="contained" color="primary" onClick={this.handleCancel}>Close</Button>,
            ]
        }

        return [
            <div key="error" style={{display: 'inline-block', color: 'red'}}>{this.state.errorMessage}</div>,
            <Button key="cancel" variant="text"
                color="primary"
                onClick={this.handleCancel}
                name="cancelButton">Cancel</Button>,
            <Button key="save" variant="contained"
                startIcon={<img className="standardIcon" src={`${process.env.PUBLIC_URL}/images/jobIcons/Job_SaveWhite.svg`}/>}
                color="primary"
                onClick={this.handleSubmit}
                name="saveButton">{'Save'}</Button>,
        ]
    }

    renderAddIngredient = () => {
        if (!this.props.disabled) {
            return (
                <Button variant="contained"
                    startIcon={<AddIcon />}
                    onClick={this.addIngredient}
                    style={{marginTop: '20px'}}
                    name="addIngredient">{'Add Ingredient'}</Button>
            )
        }
    }
    render() {
        const ingredientList = this.state.mixIngredients.map(this.renderIngredient)
        const ingredientEditDialog = this.ingredientDialog()

        const dialogActions = this.renderDialogActions()
        const carrierSection = this.getCarrierSection()
        const addIngredient = this.renderAddIngredient()

        return (
            <ReactReduxContext.Consumer>
                {((ctx) => (
                    <ResponsiveDialog
                        open={this.state.open}
                        onClose={(_, reason) => { if (reason !== 'backdropClick') this.handleSubmit() }}
                        TransitionProps={{onExited: () => { if (this.exitCallback) this.exitCallback() }}}
                        PaperProps={{className: 'wodialog'}}
                        fullWidth
                    >
                        <DialogHeader onClose={this.handleCancel}>Mix Information</DialogHeader>
                        <DialogContent>
                            <Provider store={ctx.store}>
                                <RadioGroup
                                    row
                                    name="entryMode"
                                    value={this.state.entryMode}
                                    onChange={this.handleModeChange}
                                >
                                    <FormControlLabel
                                        value="rateMode"
                                        label="Rate"
                                        control={<Radio name="rateEntryMode" disabled={this.props.disabled}/>}
                                    />
                                    <FormControlLabel
                                        value="qtyMode"
                                        label="Quantity"
                                        control={<Radio name="QuantityEntryMode" disabled={this.props.disabled}/>}
                                    />
                                </RadioGroup>
                                <ValidatedTextField
                                    label="Mix Name"
                                    fullWidth={true}
                                    onChange={this.handleTextChange}
                                    value={this.state.name}
                                    name="name"
                                    disabled={this.props.disabled}
                                    errorText={(!this.state.name && 'Required Field') || (this.state.name.length > 32 && 'Max Length 32')}
                                />
                                {carrierSection}
                                <div style={{marginTop: '20px'}}>{ingredientList}</div>
                                {addIngredient}
                                {ingredientEditDialog}
                            </Provider>
                        </DialogContent>
                        <DialogActions>{dialogActions}</DialogActions>
                    </ResponsiveDialog>
                )).bind(this)
                }
            </ReactReduxContext.Consumer>
        )
    }
}
export default connect(mapStateToProps, mapDispatchToProps)(MixEdit)
